/**
 * GET /allocation-questionnaire/strategy/status — hub step card statuses.
 *
 * Belongs here: path, axios, React Query hook, and post-POST status refresh with retries.
 * Does not belong here: hub UI or session state.
 *
 * Fetch policy: one initial load when the provider mounts. Later updates only via
 * refetchQuestionnaireStatusWithRetries after POST success.
 */
import type { QueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import type { StatusQuestionnaireApiResponse } from '../types/statusQuestionnaire.types'
import { QUESTIONNAIRE_STATUS_QUERY_KEY } from './queryKeys'
import { withRetries } from './withRetries'

export const QUESTIONNAIRE_STATUS_PATH = `${mobileBackendUrl}/v2/portfolio/allocation-questionnaire/strategy/status`
/** Retries for /status after a successful POST (submit / compute / portfolio). */
export const QUESTIONNAIRE_STATUS_REFETCH_MAX_ATTEMPTS = 3

/** GET questionnaire status for the expert-review hub. */
export async function fetchQuestionnaireStatus(): Promise<StatusQuestionnaireApiResponse> {
  const response = await axiosInstance.get<StatusQuestionnaireApiResponse>(QUESTIONNAIRE_STATUS_PATH)
  if (!response?.data) {
    throw new Error('Failed to fetch strategy questionnaire status')
  }
  return response.data
}

/** Status query — initial fetch only; no mount/focus/reconnect spam. */
export function useQuestionnaireStatusQuery() {
  return useQuery({
    queryKey: QUESTIONNAIRE_STATUS_QUERY_KEY,
    queryFn: fetchQuestionnaireStatus,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3
  })
}

/**
 * Fetch /status with retries and write into the status query cache.
 * Used only after POST success (not for the initial provider mount fetch).
 */
export async function refetchQuestionnaireStatusWithRetries(
  queryClient: QueryClient
): Promise<StatusQuestionnaireApiResponse> {
  return withRetries(
    () =>
      queryClient.fetchQuery({
        queryKey: QUESTIONNAIRE_STATUS_QUERY_KEY,
        queryFn: fetchQuestionnaireStatus,
        // Query default staleTime is Infinity — force a network read after POSTs
        staleTime: 0
      }),
    QUESTIONNAIRE_STATUS_REFETCH_MAX_ATTEMPTS
  )
}
